import React from 'react';

import { CURRENCY, isDefined } from '@nfctron/nexus-core';
import { cn, useFormat } from '@nfctron/nexus-react';
import { InfoSquareIcon } from '@nfctron/nexus-react-svg';

import { Slider, Text, Tooltip } from '@mantine/core';
import { ArrowDownUpIcon, StarIcon } from 'lucide-react';

import { getTextFieldErrorComponent } from '@root/features/form/components/TextInput/TextInput';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type Component } from '@root/lib/types/components';

import { type ChipControlProps } from './ChipControl.types';

/**
 * ChipControl component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ChipControl: Component<ChipControlProps> = ({
	error,
	isTouched,
	isValid,
	onBlur,
	onChange,
	value,
	allowZeroCharge,
	currency,
	minCharge,
	maxCharge,
	readOnly = false,
	...props
}) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON, ChipControl.displayName);
	const format = useFormat();
	const minValue = allowZeroCharge ? 0 : minCharge;
	const maxValue = maxCharge;

	const [internalValue, setInternalValue] = React.useState(value);
	const isDragging = value !== internalValue;

	/** one step on the slider based on the base currency */
	const sliderStep = React.useMemo(() => {
		switch (currency.baseCurrency.currencyId) {
			case CURRENCY.CZK:
				return 100_00;
			case CURRENCY.EUR:
				return 5_00;
			default:
				return 100_00;
		}
	}, [currency]);

	return (
		<div className="flex flex-col gap-2" {...props}>
			{/* chip card */}
			<div className={cn(['flex flex-col gap-4 rounded-xl p-4', isDefined(error) && 'bg-red-500', !isDefined(error) && 'bg-lucky-point-500'])}>
				<header>
					{/* heading */}
					<div className="flex items-start justify-between gap-3">
						<Text size="md" c="white" className="opacity-75">
							{t('heading')}
						</Text>

						{/* virtual currency helper */}
						{isDefined(currency.virtualCurrency) && (
							<Tooltip
								label={t('virtualCurrency.helperText', {
									name: currency.virtualCurrency.name,
									...(currency.virtualCurrency.rate > 1
										? {
												/** e.g. 1 bolt = 100 Kč */
												lower: format.virtualPrice(100 * currency.virtualCurrency.rate, currency.virtualCurrency),
												higher: format.price(currency.virtualCurrency.rate * 100, currency.baseCurrency.currencyId),
											}
										: {
												/** e.g. 1 Kč = 100 bolts */
												lower: format.price(100, currency.baseCurrency.currencyId),
												higher: format.virtualPrice(100, currency.virtualCurrency),
											}),
								})}
							>
								<span>
									<InfoSquareIcon className="text-lg text-white opacity-75" />
								</span>
							</Tooltip>
						)}
					</div>
					{/* chip amount */}
					<div className="flex items-start gap-3">
						<Text
							size={isDefined(currency.virtualCurrency) ? 'lg' : 'xl'}
							fw={700}
							c="white"
							className={cn('min-w-2/5 transition-transform', isDragging && 'translate-x-4 scale-110')}
						>
							{format.price(internalValue, currency.baseCurrency.currencyId)}
						</Text>

						{/* virtual currency */}
						{isDefined(currency.virtualCurrency) && (
							<React.Fragment>
								{/* swap icon */}
								<div className="grow">
									<ArrowDownUpIcon className="mx-auto rotate-90 text-xl text-white" />
								</div>

								{/* virtual currency */}
								<Text fz={20} fw={700} c="white" ta="right" className="min-w-2/5">
									{format.virtualPrice(internalValue, currency.virtualCurrency)}
								</Text>
							</React.Fragment>
						)}
					</div>
				</header>

				{/* bottom section */}
				<div className="flex flex-col gap-2">
					{/* slider */}
					<Slider
						disabled={readOnly}
						value={internalValue}
						onChange={(amount) => {
							setInternalValue(amount);
						}}
						onChangeEnd={(amount) => {
							setInternalValue(amount);
							/** outside change only after end (prevent too many state updates) */
							onChange(amount);
							onBlur();
						}}
						step={sliderStep}
						/** min/max values */
						min={minValue}
						max={maxValue}
						label={null}
						/** thumb config */
						thumbSize={32}
						thumbChildren={internalValue >= maxCharge / 2 ? <StarIcon size={Math.floor((internalValue / maxCharge) * 22)} /> : undefined}
						styles={{
							thumb: { borderWidth: 3, padding: 3, borderRadius: '50%' },
							bar: { backgroundColor: 'white' },
							track: {
								'&::before': { backgroundColor: 'rgba(255, 255, 255, 0.5)' },
							},
						}}
					/>

					{/* amount range */}
					<div className="flex w-full items-center justify-between gap-3 text-white">
						{/* min amount label */}
						<Text size="md" fw={700}>
							{format.price(minValue, currency.baseCurrency.currencyId)}
						</Text>
						{/* max amount label */}
						<Text size="md" fw={700}>
							{format.price(maxValue, currency.baseCurrency.currencyId)}
						</Text>
					</div>
				</div>
			</div>

			{/* chip card error */}
			{isDefined(error) && (
				<Text size="xs" c="red">
					{getTextFieldErrorComponent(error)}
				</Text>
			)}
		</div>
	);
};

/**
 * ChipControl component display name
 * @type {ComponentDisplayName}
 */
ChipControl.displayName = 'component/ChipControl';
